import { supabase } from '../supabase/client'
import { rememberDisplayName } from './rememberDisplayName'
import { updateUserProfile } from '../supabase/auth'
import { updateUserAvatarPreset } from '../supabase/avatars'
import { db } from '../db'
import { dispatchAppEvent } from '../events'

/**
 * Profile mutation helpers — write the current user's display fields and
 * broadcast a `user-profile-updated` event so listeners (sidebar avatar,
 * user-menu label) refetch.
 *
 * Cloud signal: an authenticated Supabase user is present. Offline signal:
 * no Supabase user → write to the local `'offline-user'` Dexie row that
 * Setup also targets (`SetupProfilePage.tsx`). The same caller-side check
 * via `useAuth().user` decides which branch the UI takes.
 *
 * Broadcasting in Electron: the multi-window relay is wired through
 * `ipcEvents.ts`; calling `dispatchAppEvent` here covers the in-process
 * listeners, and the same name is forwarded across renderer processes by
 * the preload bridge.
 */

/**
 * Resolve the currently-authenticated Supabase user id, or `null` if the app
 * is running in genuine offline mode (no client configured or no session in
 * storage).
 *
 * Crucially, this does NOT swallow transport errors: a network failure or
 * token-refresh issue throws and is surfaced by the caller's try/catch as a
 * "Save failed" toast. Returning `null` on a real error would mis-route the
 * write to the local `'offline-user'` Dexie row and silently split a
 * cloud user's data across two records.
 */
async function getCloudUserId(): Promise<string | null> {
  if (!supabase) return null
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  return data.session?.user?.id ?? null
}

export async function saveProfileName(fullName: string): Promise<void> {
  const trimmed = fullName.trim()
  if (!trimmed) return

  const cloudId = await getCloudUserId()
  if (cloudId) {
    const result = await updateUserProfile({ fullName: trimmed })
    if (!result.success) throw new Error(result.error ?? 'Update failed')
  } else {
    await db.userProfiles.update('offline-user', {
      fullName: trimmed,
      updatedAt: new Date(),
      lastModified: new Date(),
      synced: false,
    })
    localStorage.setItem('offlineUserName', trimmed)
    rememberDisplayName(trimmed)
  }
  emitProfileUpdated()
}

export async function saveProfileAvatar(avatarPresetId: string): Promise<void> {
  const cloudId = await getCloudUserId()
  if (cloudId) {
    await updateUserAvatarPreset(cloudId, avatarPresetId)
  } else {
    await db.userProfiles.update('offline-user', {
      avatar_preset_id: avatarPresetId,
    })
  }
  emitProfileUpdated()
}

export function emitProfileUpdated(): void {
  dispatchAppEvent('user-profile-updated')
  // Electron: relay to all other renderer windows via main process.
  // The preload bridge re-dispatches the same renderer-side CustomEvent on
  // receive, so listeners are uniform regardless of origin window.
  window.api?.send?.('user-profile-updated')
}

/**
 * Wire the cross-window IPC channel so a profile mutation in one renderer
 * window (main app, preferences window, …) reaches all the others as a
 * uniform in-process `user-profile-updated` event. Idempotent — call once
 * per renderer entry point at boot time.
 */
export function bootstrapProfileSyncBridge(): void {
  window.api?.receive?.('user-profile-updated', () => {
    dispatchAppEvent('user-profile-updated')
  })
}
